import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Box,
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  embedDashboard,
  type EmbeddedDashboard,
} from "@superset-ui/embedded-sdk";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { triggerDownload, useSupersetKeepalive } from "@thalamos/frontend";
import type { SupersetDashboardExportResponse } from "@thalamos/vision-common";
import {
  isCurrentUserSupervisor,
  SupersetEmbedConfigSchema,
} from "@thalamos/vision-common";

import apiClient from "../../../api/index.js";
import { config } from "../../../config/index.js";
import { useUser } from "../../../Context/UserContext.js";
import { logErrorToDatadog } from "../../../tracing.js";

type SupersetEmbedConfig = z.infer<typeof SupersetEmbedConfigSchema>;

type SupersetConfigResult =
  | { outcome: "ok"; config: SupersetEmbedConfig }
  // Server has no dashboard configured for this organisation (404).
  | { outcome: "not-configured" }
  // Live session role is not Supervisor — e.g. a multi-role user viewing this
  // page under a non-supervisor org (403).
  | { outcome: "not-authorised" };

export function Dashboard() {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const embeddedDashboardRef = useRef<EmbeddedDashboard | null>(null);
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] =
    useState<SupersetEmbedConfig | null>(null);

  // Download menu state
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const downloadMenuOpen = Boolean(downloadAnchorEl);

  const organisationId = user?.sessionContext?.organisationId;
  const isSupervisor = !!user && isCurrentUserSupervisor(user);

  const { keepaliveDialog } = useSupersetKeepalive({
    onKeepalive: async () => {
      // Calling /api/user/me extends the server-side session.
      await apiClient.user.me();
    },
    onTimeout: () => {
      window.location.href = `${config.backendUrl}/api/oauth/logout`;
    },
  });

  const handleDownloadClick = (event: MouseEvent<HTMLButtonElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleExport = async (format: "pdf" | "png") => {
    handleDownloadClose();
    if (!organisationId) return;
    setIsExporting(true);
    setExportError(null);

    try {
      let activeTabs: string[] | undefined;
      let anchor: string | undefined;
      let dataMask: Record<string, unknown> | undefined;
      if (embeddedDashboardRef.current) {
        try {
          activeTabs = await embeddedDashboardRef.current.getActiveTabs();
          anchor = activeTabs[0];
        } catch (err) {
          // If we can't determine the active tabs (e.g. dashboard not initialised),
          // fall back to exporting the default dashboard instead of failing the export.
          logErrorToDatadog(
            err instanceof Error
              ? err
              : new Error(`Failed to get active Superset tabs: ${String(err)}`),
          );
        }
        try {
          dataMask = await embeddedDashboardRef.current.getDataMask();
        } catch (err) {
          // If we can't retrieve the current filter state, fall back to
          // exporting without filters rather than failing the export.
          logErrorToDatadog(
            err instanceof Error
              ? err
              : new Error(`Failed to get Superset dataMask: ${String(err)}`),
          );
        }
      }

      const response = await apiClient.report.exportSupersetDashboard(
        organisationId,
        { format, activeTabs, anchor, dataMask },
      );

      const { data, contentType, filename } =
        response.data as SupersetDashboardExportResponse;

      // Convert base64 to data URI and trigger download
      const dataUri = `data:${contentType};base64,${data}`;
      triggerDownload(dataUri, filename);
    } catch (err) {
      const axiosError = err as AxiosError<{ reason?: string }>;
      const message =
        axiosError.response?.data?.reason ??
        axiosError.message ??
        t("supervisor.dashboard.exportError");
      setExportError(message);
      logErrorToDatadog(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsExporting(false);
    }
  };

  const fetchSupersetConfig =
    useCallback(async (): Promise<SupersetConfigResult> => {
      if (!organisationId) {
        throw new Error("No organisation ID available");
      }
      const response =
        await apiClient.report.getSupersetDashboard(organisationId);
      // The API client resolves 4xx responses (validateStatus < 500) rather
      // than throwing, so branch on the status before parsing the body.
      if (response.status === 404) {
        return { outcome: "not-configured" };
      }
      if (response.status === 403) {
        return { outcome: "not-authorised" };
      }
      if (response.status !== 200) {
        throw new Error(
          `Dashboard config request failed with status ${String(response.status)}`,
        );
      }
      const parsed = SupersetEmbedConfigSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new Error(
          `Invalid dashboard config response: ${parsed.error.message}`,
        );
      }
      return { outcome: "ok", config: parsed.data };
    }, [organisationId]);

  const fetchGuestToken = useCallback(async (): Promise<string> => {
    try {
      const result = await fetchSupersetConfig();
      if (result.outcome !== "ok") {
        throw new Error(`Dashboard config unavailable (${result.outcome})`);
      }
      return result.config.guestToken;
    } catch (err) {
      logErrorToDatadog(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }, [fetchSupersetConfig]);

  useEffect(() => {
    // Only fetch the dashboard config for supervisors. Otherwise the request
    // would fail server-side; skipping it lets us show a clear message instead
    // (e.g. a supervisor who has switched to a non-supervisor role/context).
    if (!organisationId || !isSupervisor) {
      setLoading(false);
      setDashboardData(null);
      setError(null);
      return;
    }

    // Guards against out-of-order responses: if organisationId/context changes
    // mid-flight, the superseded request must not clobber the latest state.
    let cancelled = false;

    const fetchDashboardData = async () => {
      // Clear previous state before (re)fetching, e.g. on an organisation/context
      // change. This shows a spinner rather than a stale dashboard while in
      // flight, and clearing dashboardData drives the embed effect to unmount any
      // previously embedded dashboard (including when the new outcome is not ok).
      setLoading(true);
      setError(null);
      setDashboardData(null);
      try {
        const result = await fetchSupersetConfig();
        if (cancelled) return;
        if (result.outcome === "ok") {
          setDashboardData(result.config);
        } else {
          setError(result.outcome);
        }
      } catch (err) {
        if (cancelled) return;
        logErrorToDatadog(err instanceof Error ? err : new Error(String(err)));
        setError(
          err instanceof Error
            ? err.message
            : t("supervisor.dashboard.fetchError"),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchDashboardData();

    return () => {
      cancelled = true;
    };
  }, [organisationId, isSupervisor, t, fetchSupersetConfig]);

  useEffect(() => {
    const container = containerRef.current;
    if (!dashboardData || !container) {
      return;
    }

    const { dashboardId, supersetUrl } = dashboardData;

    let isMounted = true;

    embedDashboard({
      id: dashboardId,
      supersetDomain: supersetUrl,
      mountPoint: container,
      fetchGuestToken,
      dashboardUiConfig: {
        hideTitle: true,
        hideChartControls: false,
        filters: {
          expanded: true,
        },
      },
    })
      .then((dashboard) => {
        if (isMounted) {
          embeddedDashboardRef.current = dashboard;
        } else {
          // Component unmounted before embed resolved - clean up immediately
          dashboard.unmount();
        }
      })
      .catch((err: unknown) => {
        if (isMounted) {
          logErrorToDatadog(
            err instanceof Error ? err : new Error(String(err)),
          );
        }
      });

    return () => {
      isMounted = false;
      if (embeddedDashboardRef.current) {
        embeddedDashboardRef.current.unmount();
        embeddedDashboardRef.current = null;
      }
    };
  }, [dashboardData, fetchGuestToken]);

  if (!isSupervisor) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{t("supervisor.dashboard.notSupervisor")}</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error === "not-configured") {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{t("supervisor.dashboard.notConfigured")}</Typography>
      </Box>
    );
  }

  if (error === "not-authorised") {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{t("supervisor.dashboard.notSupervisor")}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          {t("supervisor.dashboard.errorLoading", { error })}
        </Typography>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{t("supervisor.dashboard.noData")}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "85vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {keepaliveDialog}
      {/* Header with download button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          px: 4,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {exportError && (
          <Box sx={{ color: "error.main", mr: 2, fontSize: "0.875rem" }}>
            {exportError}
          </Box>
        )}
        <Button
          id="download-button"
          variant="outlined"
          startIcon={
            isExporting ? <CircularProgress size={16} /> : <DownloadIcon />
          }
          endIcon={<ArrowDownIcon />}
          onClick={handleDownloadClick}
          disabled={isExporting}
          aria-controls={downloadMenuOpen ? "download-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={downloadMenuOpen ? "true" : undefined}
        >
          {isExporting
            ? t("supervisor.dashboard.exporting")
            : t("supervisor.dashboard.download")}
        </Button>
        <Menu
          id="download-menu"
          anchorEl={downloadAnchorEl}
          open={downloadMenuOpen}
          onClose={handleDownloadClose}
          slotProps={{
            list: {
              "aria-labelledby": "download-button",
            },
          }}
        >
          <MenuItem onClick={() => void handleExport("pdf")}>
            <ListItemIcon>
              <PdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("supervisor.dashboard.downloadPdf")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => void handleExport("png")}>
            <ListItemIcon>
              <ImageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t("supervisor.dashboard.downloadImage")}
            </ListItemText>
          </MenuItem>
        </Menu>
      </Box>
      {/* Dashboard container */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          width: "100%",
          "& iframe": {
            width: "100%",
            height: "100%",
            border: "none",
          },
        }}
      />
    </Box>
  );
}
